import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface MergeClientsResult {
  ok: boolean;
  alreadyMerged: boolean;
  eventsRepointed?: number;
  contactsMoved?: number;
  loanIdsAfter?: number;
}

interface SplitClientResult {
  ok: boolean;
  alreadySplit: boolean;
  splitClientId?: string;
  movedLoanCount?: number;
  primaryLoanCountAfter?: number;
}

/**
 * Admin identity tooling — wraps the `mergeClients` / `splitClient` callables
 * (super_admin/administrator only; the backend re-enforces the role gate). The
 * UI gates visibility too, but authorization is enforced server-side.
 */
export function useClientIdentityTools(companyId?: string) {
  const [busy, setBusy] = useState(false);

  const mergeClients = useCallback(
    async (survivorClientId: string, mergedClientId: string): Promise<MergeClientsResult> => {
      if (!companyId) throw new Error('companyId is required');
      setBusy(true);
      try {
        const fn = httpsCallable<Record<string, unknown>, MergeClientsResult>(functions, 'mergeClients');
        const res = await fn({ companyId, survivorClientId, mergedClientId });
        return res.data;
      } finally {
        setBusy(false);
      }
    },
    [companyId],
  );

  const splitClient = useCallback(
    async (params: {
      clientId: string;
      clusterAnchorLoanId: string;
      loanIds: string[];
      resolvesAmbiguity?: boolean;
    }): Promise<SplitClientResult> => {
      if (!companyId) throw new Error('companyId is required');
      setBusy(true);
      try {
        const fn = httpsCallable<Record<string, unknown>, SplitClientResult>(functions, 'splitClient');
        const res = await fn({ companyId, ...params });
        return res.data;
      } finally {
        setBusy(false);
      }
    },
    [companyId],
  );

  return { mergeClients, splitClient, busy };
}
